import { useState } from 'react';
import Modal from '../../_custom/Modal/Modal';
import Button from '../../_custom/Button/Button';
import Checkbox from '../../_custom/Checkbox/Checkbox';
import RadioButton from '../../RadioButton/RadioButton';
import { type PasteMode } from './pasteSpecial';
import modalOverDrawerStyles from '../../ConfirmDialog/modalOverDrawer.module.scss';
import styles from './PasteSpecialDialog.module.scss';

const PASTE_MODE_LABELS: Record<PasteMode, string> = { all: 'All', values: 'Values', formats: 'Formats' };

export interface PasteSpecialDialogProps {
    open: boolean;
    isInternalSource: boolean;
    onClose: () => void;
    onApply: (mode: PasteMode, transpose: boolean) => void;
}

/** All / Values / Formats + Transpose. Formats only makes sense for an
 *  internal copy — an external clipboard has no source style/format to carry
 *  over, so it's disabled there and the mode falls back to Values. */
const PasteSpecialDialog: React.FC<PasteSpecialDialogProps> = ({ open, isInternalSource, onClose, onApply }) => {
    const [mode, setMode] = useState<PasteMode>('all');
    const [transpose, setTranspose] = useState(false);

    const effectiveMode = !isInternalSource && mode === 'formats' ? 'values' : mode;

    return (
        <Modal open={open} onClose={onClose} size="flexible" className={modalOverDrawerStyles.modalOverDrawer} withoutCloseButton>
            <div className={styles.Dialog}>
                <span className={styles.Title}>Paste special</span>
                <div className={styles.Options} role="radiogroup" aria-label="Paste mode">
                    {(['all', 'values', 'formats'] as PasteMode[]).map((m) => {
                        const disabled = m === 'formats' && !isInternalSource;
                        return (
                            <RadioButton
                                key={m}
                                name="paste-mode"
                                label={PASTE_MODE_LABELS[m]}
                                checked={mode === m}
                                disabled={disabled}
                                // RadioButton also fires onChange(false) when its already-checked
                                // radio is clicked again — ignore that so a mode is never un-set.
                                onChange={(isChecked) => isChecked && setMode(m)}
                            />
                        );
                    })}
                    <Checkbox label="Transpose" checked={transpose} onChange={setTranspose} />
                </div>
                <div className={modalOverDrawerStyles.actions}>
                    <Button text="Cancel" variant="purpleinverted" onClick={onClose} className={modalOverDrawerStyles.actionBtn} />
                    <Button
                        text="Apply"
                        variant="primary"
                        onClick={() => onApply(effectiveMode, transpose)}
                        className={modalOverDrawerStyles.actionBtn}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default PasteSpecialDialog;
